import { type Action, type Dispatch, type ThunkDispatch } from '@reduxjs/toolkit';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { AuthNewLocalStorage } from '@pkg/components/Web3Auth/enums';
import { useAppDispatch } from '@pkg/hooks';
import { type TwoFactorAuthType } from '@pkg/types/enums';

import { apiFactory } from '@app/client/apiFactory/api';
import {
    type ApiResult,
    type MagicLinkResponseValue,
    type PasswordResetEmailResponseValue,
    type Token,
} from '@app/client/common/structs';
import { Events } from '@app/client/common/structs/events/events';
import { Auth } from '@app/client/resources';
import { prefetch2FA, runAllTwoFactorCallbacks } from '@app/helpers';
import SystemRedirect, { RedirectType } from '@app/helpers/classes/Redirect';
import { AUTH_SIGN_IN } from '@app/routes/paths/auth';
import { setTokens } from '@app/store/zustand/Auth/useUserTokens';

import { type RootState } from '../reducers';
import { hide2faExpiredLogin } from '../slices';
import {
    ACTION_REMOVE_TOKENS,
    ACTION_SET_LOADING_USER,
    ACTION_SET_MAGIC_LINK_EMAIL,
    ACTION_SET_TOKENS,
    type AuthActionTypes,
} from '../types/auth';

import { setError } from './error';
import { registerEvent } from './events';
import { initializeUser } from './user';
import { setCurrentUserEmoji, setRemovedUserEmojis, setUserExtendedEmojis } from './user-emojis';

export const setAuthTokens = (tokens: Token, isDraftUser?: boolean): AuthActionTypes => ({
    type: ACTION_SET_TOKENS,
    tokens,
    isDraftUser,
});

export const setLoadingUser = (loadingUser: boolean): AuthActionTypes => ({
    type: ACTION_SET_LOADING_USER,
    loadingUser,
});

export const removeTokens = (): AuthActionTypes => ({
    type: ACTION_REMOVE_TOKENS,
});

export function setMagicLinkEmail(magicLinkEmail: string): AuthActionTypes {
    return {
        type: ACTION_SET_MAGIC_LINK_EMAIL,
        magicLinkEmail,
    };
}

export function login(email: string, password: string, callback?: () => void) {
    return async function (
        dispatch: ThunkDispatch<RootState, void, Action>,
        state: () => RootState,
    ): Promise<ApiResult<Token>> {
        dispatch(setLoadingUser(true));
        apiFactory().ws?.closeWithoutReconnect();
        const partnerConversionId = state().partnerIntegrations.partnerConversionId;
        return apiFactory()
            .call(Auth.login, {
                value: { email: email.trim(), password: password.trim(), partner_conversion_id: partnerConversionId },
            })
            .then((r) => {
                if (r.ok) {
                    // Make sure to do session cleanup of emojis removal notification related data in order to prevent
                    // display of unrelated/outdated notification
                    dispatch(setUserExtendedEmojis([]));
                    dispatch(setRemovedUserEmojis([]));
                    localStorage.removeItem('emojiIds');
                    dispatch(setAuthTokens(r.value));
                    if (!r.value.requires_2fa || r.value.requires_2fa === 'Invalid') {
                        initializeUser()(dispatch, state);
                    }
                } else {
                    dispatch(setError({ ...r, notify: true }));
                }
                dispatch(setLoadingUser(false));
                return r;
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
                dispatch(removeTokens());
                dispatch(setLoadingUser(false));
                return e;
            })
            .finally(() => {
                dispatch(setLoadingUser(false));
                callback?.();
            });
    };
}

export function login2FA(code: string, refresh_token: string, callback?: (tokens?: string) => void) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<Token> | void> {
        return apiFactory()
            .call(Auth.login2FA, { value: { code, refresh_token } })
            .then((r) => {
                if (r.ok) {
                    setTokens(r.value);
                    dispatch(setAuthTokens(r.value));
                    dispatch(hide2faExpiredLogin());
                    dispatch(initializeUser());
                    runAllTwoFactorCallbacks();
                    callback?.(r.value.access_token);
                } else {
                    dispatch(setError({ ...r, notify: true }));
                    callback?.();
                }
                return r;
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
                callback?.();
                return e;
            });
    };
}

export function magicLink(email: string, redirect?: string) {
    return async function (
        dispatch: ThunkDispatch<RootState, void, Action>,
    ): Promise<ApiResult<MagicLinkResponseValue>> {
        return apiFactory()
            .call(Auth.magicLink, { value: { email: email.trim(), redirect } })
            .then((r) => {
                dispatch(setMagicLinkEmail(email));
                return r;
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
                return e;
            });
    };
}

export function logout() {
    return function (dispatch: Dispatch): void {
        dispatch(setCurrentUserEmoji(''));
        dispatch(removeTokens());
    };
}
function clearSession() {
    apiFactory().disconnectWebsocket();
    localStorage.removeItem('ftue');
    localStorage.removeItem('emojiIds');
    localStorage.setItem('removeDraftUser', '0');
    localStorage.removeItem(AuthNewLocalStorage.OAuthLoggedIn);
}

export function useLogout() {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    return (storedRedirectPath?: string | null) => {
        clearSession();
        dispatch(logout());
        if (storedRedirectPath) {
            SystemRedirect.setRedirect(RedirectType.Default, storedRedirectPath);
        }
        void queryClient.invalidateQueries();
        navigate(AUTH_SIGN_IN);
    };
}

export function disable2FA(provider: TwoFactorAuthType, disable_all = true) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        return apiFactory()
            .call(Auth.disable2FA, { prefetch: prefetch2FA(dispatch), value: { disable_all, provider } })
            .then((r) => {
                if (r.ok) {
                    clearSession();
                    dispatch(logout());
                } else {
                    dispatch(setError({ ...r, notify: true }));
                }
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
            });
    };
}

export function passwordResetEmail(email: string) {
    return async function (
        dispatch: ThunkDispatch<RootState, void, Action>,
    ): Promise<ApiResult<PasswordResetEmailResponseValue>> {
        return apiFactory()
            .call(Auth.passwordResetEmail, { value: { email: email.trim() } })
            .then((r) => {
                if (!r.ok) {
                    dispatch(setError({ ...r, notify: true }));
                }
                return r;
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
                return e;
            });
    };
}

export function passwordReset(password: string, passwordResetToken: string) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<Token>> {
        return apiFactory()
            .call(Auth.passwordReset, { value: { password, password_reset_token: passwordResetToken } })
            .then((r) => {
                if (!r.ok) {
                    dispatch(setError({ ...r, notify: true }));
                }
                return r;
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
                return e;
            });
    };
}

export function createAccount(email: string, password: string, callback?: () => void, errorFeedback = true) {
    return async function (
        dispatch: ThunkDispatch<RootState, void, Action>,
        getState: () => RootState,
    ): Promise<ApiResult<Token>> {
        dispatch(setLoadingUser(true));
        apiFactory().ws?.closeWithoutReconnect();
        const partnerConversionId = getState().partnerIntegrations.partnerConversionId;

        return apiFactory()
            .call(Auth.createAccount, {
                value: { email: email.trim(), password: password.trim(), partner_conversion_id: partnerConversionId },
            })
            .then((r) => {
                if (r.ok) {
                    // Make sure to do session cleanup of emojis removal notification related data in order to prevent
                    // display of unrelated/outdated notification
                    dispatch(setUserExtendedEmojis([]));
                    dispatch(setRemovedUserEmojis([]));
                    localStorage.removeItem('emojiIds');

                    dispatch(magicLink(email.trim()));

                    // TODO: Uncomment the line below if we want to log in automatically
                    // dispatch(setTokens(r.value));
                    registerEvent(Events.user_signup);
                    callback && callback();
                } else {
                    /**
                     * TODO: Remove this when the BE standardaizes response for create account
                     *
                     * Error comes as an object of arrays rather than as an array
                     * {
                     *  "error": "Validation error",
                     *  "fields": {
                     *      "email": [
                     *          {
                     *              "code": "uniqueness",
                     *              "message": "Email must be unique"
                     *          }
                     *      ]
                     *  }
                     */
                    // TODO: Fix types
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const fields = r.fields && Object.values(r.fields).map((f: any) => f[0].message);

                    if (errorFeedback) {
                        dispatch(setError({ ...r, notify: true, fields }));
                    }

                    dispatch(removeTokens());
                }

                dispatch(setLoadingUser(false));

                return r;
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
                dispatch(removeTokens());
                dispatch(setLoadingUser(false));

                throw e;
            });
    };
}

export function loginWithToken(token: Token) {
    return function (dispatch: ThunkDispatch<RootState, void, Action>, state: () => RootState) {
        dispatch(setLoadingUser(true));
        apiFactory().ws?.closeWithoutReconnect();

        dispatch(setUserExtendedEmojis([]));
        dispatch(setRemovedUserEmojis([]));
        localStorage.removeItem('emojiIds');

        dispatch(setAuthTokens(token));
        if (!token.requires_2fa || token.requires_2fa === 'Invalid') {
            initializeUser()(dispatch, state);
        }
    };
}
